<!--
 * @Author: zhubincong
 * @Date: 2020-3-9 19:41:31
 * @Desc: 运营专员预警审核数量排名
 -->
<template>
  <ViewPanel title="运营专员预警审核数量排名" scroll :active="active">
    <div v-show="!noData" ref="chart" class="chart"></div>
    <div v-show="noData" class="no-data">
      暂无数据
    </div>
  </ViewPanel>
</template>

<script>
import ViewPanel from '../Components/ViewPanel'
import G2 from '@antv/g2'
import * as api from '@/api'
import utils from '@/utils'
import { isIe } from '@hui-pro/utils'

export default {
  name: 'WarnNumberRanking',
  components: { ViewPanel },
  inject: ['$riskMonitor'],
  props: {
    geoData: {
      type: Object,
      default: () => {}
    },

    // 屏幕尺寸类型
    sizeMode: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      warnNumberList: [],

      // 图表尺寸
      chartSize: {
        sm: {
          width: 280,
          height: 140,
          xAxisFontSize: 12,
          yAxisFontSize: 10
        },
        md: {
          width: 360,
          height: 230,
          xAxisFontSize: 14,
          yAxisFontSize: 12
        },
        lg: {
          width: 400,
          height: 280,
          xAxisFontSize: 16,
          yAxisFontSize: 14
        }
      },
      chart: null
    }
  },
  computed: {
    active() {
      return this.$riskMonitor.activePanel.toLowerCase() === this.$options.name.toLowerCase()
    },
    /**  显示没有数据 */
    noData() {
      return this.warnNumberList.length === 0
    }
  },
  watch: {
    geoData() {
      this.chart = null // g2 chart对象
      this.renderChart()
    },
    sizeMode(data) {
      this.changeChartSize()
    }
  },
  methods: {
    /**
     * 渲染排名图表
     * @author zhubincong
     * @date 2020-03-16 19:05:00
     */
    async renderChart() {
      await this.getData()
      const { width, height, xAxisFontSize, yAxisFontSize } = this.chartSize[this.sizeMode]

      const chart = new G2.Chart({
        container: this.$refs.chart,
        width,
        height,
        padding: 'auto'
      })
      this.chart = chart
      // 数据
      chart.source(this.warnNumberList)

      // 坐标
      this.setAxis(chart, xAxisFontSize, yAxisFontSize)

      // 不显示tooltip
      chart.tooltip(false)

      // 坐标系
      chart
        .coord()
        .transpose()
        .reflect('y')
      // 不显示图例
      chart.legend(false)
      // 几何标记
      chart
        .interval()
        .position('operationPersonName*number')
        .size(16)
        .color('number*label', (number, label) => {
          if (label === 'TOP 1') {
            return 'l(0) 0:#FFB023 1:#FFF823'
          } else {
            return 'l(0) 0:#00FFED 1:#CFFFFE'
          }
        })
        .shape('dashLine')
        .active(false)
      chart.render()
    },

    /**
     * @author xuchen
     * @date 2020-04-27 18:17:23
     * @desc 设置axis配置
     */
    setAxis(chart, xAxisFontSize, yAxisFontSize) {
      chart.axis('operationPersonName', {
        label: {
          textStyle: {
            fill: '#8FEEFF',
            fontSize: xAxisFontSize
          }
        },
        line: null,
        tickLine: null
      })
      chart.axis('number', {
        label: {
          textStyle: {
            fill: '#8FEEFF',
            fontSize: yAxisFontSize
          }
        },
        // 网格线
        grid: {
          lineStyle: {
            stroke: isIe() ? '#059e9e' : 'l(90) 0:#00fff7b3 1:#357e9333',
            lineDash: [1, 0]
          },
          hideFirstLine: false
        }
      })
    },

    /**
     * 查询运营专员事件处理排名
     * @author zhubincong
     * @date 2020-03-16 17:22:40
     */
    async getData() {
      const { data } = await api.emmsRiskMonitorQueryOperationPersonRank({
        data: { projectCode: this.$store.state.projectCode, count: 5 }
      })

      // 随机生成区域内经纬度
      this.warnNumberList = utils.getRandomPoint4List(data.list, this.geoData).map((n, i) => {
        n.label = 'TOP ' + (i + 1)
        return n
      })
    },

    /**
     * 渲染地图
     * @author zhubincong
     * @date 2020-03-11 18:47:37
     */
    renderMap() {
      const { mapChart, mapView } = this.$riskMonitor
      // 清除其他业务图表
      mapView.clear()
      if (this.warnNumberList.length === 0) {
        mapChart.repaint()
        return
      }
      mapView.source(this.warnNumberList)
      mapView
        .point()
        .position('longitude*latitude')
        .size(0)
        .tooltip('operationPersonName*number', (name, value) => ({ name, value }))
        // .shape('operationPersonName', () => ['image', face])
        .label('operationPersonName', {
          useHtml: true,
          htmlTemplate(name, { point }, index) {
            return `<div class="duty-map-person ${
              index === 0 ? 'duty-map-person-first' : ''
            }"><p style="color:#000000;">${point.label}</p><p>${name}</p></div>`
          },
          offset: 80
        })
      mapChart.legend(false)
      mapChart.repaint()
    },
    /**
     * 刷新数据
     * @author zhubincong
     * @date 2020-03-18 17:12:42
     */
    async refreshData() {
      await this.getData()
      this.chart.changeData(this.warnNumberList)
      if (this.active) this.renderMap()
    },

    /**
     * @author xuchen
     * @date 2020-04-26 19:46:58
     * @desc 修改环形图大小
     */
    changeChartSize() {
      if (this.chart) {
        const { width, height, xAxisFontSize, yAxisFontSize } = this.chartSize[this.sizeMode]
        this.chart.changeSize(width, height)
        this.setAxis(this.chart, xAxisFontSize, yAxisFontSize)
        this.chart.repaint()
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.no-data {
  background: url('~@/assets/images/RiskMonitoring/nodata3.png') no-repeat top 50px center;
  padding-top: 176px;
  font-size: 24px;
  color: #5f98a2;
  text-align: center;
}
</style>
<style lang="scss">
.duty-map-person {
  background: url('~@/assets/images/RiskMonitoring/person.png') no-repeat;
  width: 77px;
  height: 154px;
  text-align: center;
  position: relative;
  &.duty-map-person-first {
    background: url('~@/assets/images/RiskMonitoring/person-first.png') no-repeat;
  }
}
.duty-map-person:hover {
  z-index: 10;
}
</style>

<style lang="scss" scoped>
@media screen and (min-width: 1900px) {
  .view-panel {
    height: 376px;
  }
}

@media screen and (min-width: 1600px) and (max-width: 1899px) {
  .view-panel {
    width: 383px;
    height: 313px;
  }
  .no-data {
    font-size: 16px;
  }
}

@media screen and (max-width: 1599px) {
  .view-panel {
    width: 306px;
    height: 250px;
  }
  .no-data {
    padding-top: 130px;
    font-size: 14px;
    background-size: auto 71%;
    background-position: center center;
  }
}
</style>
